<template lang="pug">
div(v-if="isTestUser")
.side.relative.flex.flex-col.w-11.h-full.justify-center(v-else, :class="[{ expand: isExpand }, 'portrait:w-20 portrait:flex portrait:flex-col portrait:items-center portrait:h-full portrait:pb-24']")
  button.flex.flex-col.items-center.outSide-backward.relative.overflow-hidden(v-if="(getBackwardRoom && showBackward(getBackwardRoom)) || getGameSearchPageEnable", @click="backward")
    img.w-full.-translate-y-1.scale-80(:src="idxcategoryIconForwardBoth", :alt="getRoomName(getBackwardRoom)")
  button.menu-button(type="button", :style="{ '--bg': `url(${menuBtnImg})`, '--expand-bg': `url(${closeBtnImg})` }", @click="toggle")
  button.flex.flex-col.items-center.search-button.relative.mt-2.overflow-hidden(v-if="isGameSearchEnable && !getGameSearchPageEnable", @click="gameSearchClickHandler")
    img.w-full.-translate-y-1.scale-80(:src="idxcategoryIconSearchBoth")
  .mx-auto.mt-1.text-primary-text(class="portrait:flex-1 portrait:overflow-hidden portrait:mt-2 portrait:w-full")
    Footbar(orientation="vertical")
  .backdrop.hidden.fixed.inset-0(class="bg-black/80", @click="cancel")
  .menu-container.fixed.overflow-hidden.transition-all.w-0.z-10.start-0.transform.h-full(class="portrait:transform-none top-0")
    .menu-outer-frame.h-full.me-2
      .menu-inner-frame.flex.flex-col.h-full
        .relative.side-logo.h-14.cursor-pointer.self-center.mt-5.mb-3(@click="clickLogo")
          img.w-full.h-full.object-contain(:src="logoUrl")
        ul.overflow-y-auto
          MenuItem(v-for="item in menuList", ref="menu", :item="item", :menuItemStack="menuItemStack", :historyStack="historyStack", :key="item.categoryId", @click="select", @backward="backward")
</template>

<script setup>
import { useRuntimeConfig } from '#app'
import { ref, computed, onMounted, getCurrentInstance, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from '#imports'
import { useMainStore } from '@/stores/main'
import { isTrue } from '@/utils/siteConfigChecker'
import idxcategoryIconSearchBoth from 'assets/images/side/idxcategory_icon_search_both.png'
import idxcategoryIconForwardBoth from 'assets/images/side/idxcategory_icon_forward_both.png'

const { getBackCdnUrl, getFrontCdnUrl } = useCdnAssets()
const { verify } = useVerify()

// 動態多語系圖片
const menuBtnImg = computed(() => getFrontCdnUrl('images/side/idxcategory_btn_menu_{locale}_normal.png'))
const closeBtnImg = computed(() => getFrontCdnUrl('images/side/idxcategory_btn_close_{locale}_normal.png'))

const props = defineProps({
  menu: Object,
  menuItemStack: Array,
  historyStack: Array,
})

const emit = defineEmits(['clickLogo', 'play'])

const instance = getCurrentInstance()
const { locale } = useI18n()
const $SoundControl = instance.appContext.config.globalProperties.$SoundControl
const $analytics = instance.appContext.config.globalProperties.$analytics

const { isGuest } = useAuthToken()
const sideMenu = useSideMenu()
const { selectMenuItem, popHistory } = sideMenu
const useGameComposable = useGame()
const { userInfo } = useUserInfo({ autoInit: false })
const { checkGameRestriction } = useGameRestriction()

const mainStore = useMainStore()
const { getWithdrawRule, getTrackLocation, getGameSearchPageEnable } = storeToRefs(mainStore)
const { setModal, setGameSearchPageEnable: setGameSearchPageEnableAction } = mainStore

const { gameSearchEnable } = useSite()

const isExpand = ref(false)
const getBackwardRoom = ref(null)
const logoUrl = ref(null)

const isGameSearchEnable = computed(() => isTrue(gameSearchEnable.value))
const isTestUser = computed(() => userInfo.value?.scope === 'testuser')

const menuList = computed(() => {
  return (props.menu?.data || []).map((item) => {
    const disabled =
      item.isBonus && (userInfo.value.withdraw_rule?.indexOf('FREE_LIMIT_') === -1 || (getWithdrawRule.value?.limitGameDetail || []).length === 0)
    return { ...item, disabled }
  })
})

const gameSearchClickHandler = () => {
  if (isGuest.value) {
    setModal('ModalGuestLogin')
    return
  }
  setGameSearchPageEnableAction(true)
}

const showBackward = (room) => {
  if (props.historyStack.some((item) => item.categoryId === room.categoryId)) {
    getBackwardRoom.value = room
  }
  return props.historyStack.some((item) => item.categoryId === room.categoryId)
}

const cancel = () => {
  $SoundControl.sound.play('close')
  isExpand.value = false
}

const play = (game) => {
  $SoundControl.sound.play('click')
  verify('ENTER_GAME').then((isVerified) => {
    if (isVerified) {
      $analytics.push({
        event: 'game_click',
        location: getTrackLocation.value,
        locationId: null,
        provider: game.providerId,
        gameId: game.gameId,
        gameName: game.name['en-US'],
      })
      emit('play')
      nextTick(() => {
        useGameComposable.playGame({ game, locale: locale.value })
      })
    }
  })
}

const select = (item) => {
  if (item.isGame) {
    // 未登入則打開登入註冊彈窗
    if (isGuest.value) {
      setModal('ModalGuestLogin')
      return
    }

    if (checkGameRestriction(item)) {
      return
    }
    play(item)
  } else {
    $analytics.push({
      event: 'lobby_click',
      location: 'lobby',
      icon: item.name?.['en-US'],
      iconId: item.categoryId,
    })

    // 如果選到的不是最外層
    if (item.parents?.length) {
      // 切換選單時，記錄從最外層開始更新，由於 parents 只有在這裡用所以存入前移除
      item.parents.forEach((parent) => {
        delete parent.parents
        selectMenuItem(parent)
      })
    }

    delete item.parents
    selectMenuItem(item)
    useGameComposable.setRoom(item)
    isExpand.value = false
  }
}

const backward = () => {
  if (getGameSearchPageEnable.value) {
    setGameSearchPageEnableAction(false)
  } else {
    getBackwardRoom.value = null
    useGameComposable.setRoom(props.historyStack.slice(-1)[0])
    popHistory()
  }
}

const clickLogo = () => {
  emit('clickLogo')
  isExpand.value = false
}

const toggle = () => {
  isExpand.value = !isExpand.value
}

const getRoomName = (item) => {
  if (!item) {
    return ''
  }
  return item.name?.[locale.value] || item.name?.['en-US']
}

onMounted(() => {
  logoUrl.value = getBackCdnUrl('icon/logo.png')

  if (useGameComposable.room?.value && useGameComposable.room?.value?.categoryId) {
    useGameComposable.recoverSetRoom(useGameComposable.room.value)
  } else {
    const lastItem = props.menuItemStack.slice(-1)[0]
    useGameComposable.setRoom(lastItem)
  }
})
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

.backdrop {
  display: none;
}

.expand .backdrop {
  display: block;
}

.expand .menu-container {
  width: calc(13.25rem + env(safe-area-inset-left));
}

.menu-outer-frame {
  @apply relative border-menu-field-border bg-linear-to-b from-menu-field-from to-menu-field-to;
  border-inline-end-width: 0.2rem;
  border-inline-end-style: solid;
  inset: 0rem;
}

.menu-outer-frame::before {
  content: '';
  position: absolute;
  border-inline-end: 0.0625rem solid #ffffffcc;
  inset: 0rem;
  inset-inline-end: -0.125rem;
}

.menu-inner-frame {
  @apply relative bg-linear-to-b from-menu-bg-from to-menu-bg-to border-menu-bg-border text-menu-bg-border me-1;
  border-inline-end-width: 0.2rem;
  border-inline-end-style: solid;
}

.menu-inner-frame > ul {
  background: -moz-radial-gradient();
  margin-inline-start: env(safe-area-inset-left);
}

.menu-inner-frame::before {
  content: '';
  position: absolute;
  border-inline-end: 0.0625rem solid #ffffff7f;
  inset: -0.5rem;
  inset-inline-end: -0.125rem;
}

.menu-inner-frame .side-logo {
  margin-inline-start: env(safe-area-inset-left);
}

.menu-button {
  @apply relative z-10 bg-no-repeat bg-contain transition w-10;
  aspect-ratio: 38 / 94;
  background-image: var(--bg);
}

@media (orientation: portrait) {
  .menu-button {
    @apply ms-0.5 w-12;
  }
}

.expand .menu-button {
  background-image: var(--expand-bg);
  transform: translate(13.25rem);
}

@media (orientation: portrait) {
  .menu-button {
    top: 0;
    transform: translateY(0);
  }

  .expand .menu-button {
    transform: translate(13.25rem, 0);
  }
}

.search-button {
  @apply w-8 ms-1 aspect-square bg-no-repeat bg-contain;
  background-image: url('assets/images/side/idxcategory_img_single_normal.png');
}

@media (orientation: portrait) {
  .search-button {
    @apply w-9 ms-0.5;
  }
}

.outSide-backward {
  @apply w-8 ms-1 aspect-square bg-no-repeat bg-contain mb-2;
  background-image: url('assets/images/side/idxcategory_img_single_normal.png');
}

@media (orientation: portrait) {
  .outSide-backward {
    @apply w-9 ms-0.5;
  }
}

.side {
  z-index: 10;
}

.side.expand {
  z-index: 35;
}
</style>
